import React, { useState, useEffect } from 'react';

const AdminDeadlineSetter = ({
  deadlines,
  extensions,
  onSetDeadline,
  onNotify,
  onSetExtension,
  onRemoveExtension,
  onSendReminder,
  users,
}) => {
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const [deadline, setDeadline] = useState(deadlines[currentMonth] || '');
  const [extensionForm, setExtensionForm] = useState({ companyId: '', period: currentMonth, date: '' });
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  useEffect(() => {
    setDeadline(deadlines[currentMonth] || '');
  }, [deadlines, currentMonth]);
  
  const handleSetDeadline = async () => {
    if (!deadline) {
      onNotify('Por favor, selecciona una fecha.', 'error');
      return;
    }
    
    try {
      await onSetDeadline(currentMonth, deadline);
      onNotify(`Fecha límite para ${currentMonth} establecida con éxito.`, 'success');
    } catch (error) {
      onNotify(error.message || 'No se pudo guardar la fecha límite.', 'error');
    }
  };

  const handleSetExtension = async () => {
    if (!extensionForm.companyId || !extensionForm.period || !extensionForm.date) {
      onNotify('Selecciona una empresa, un período y una nueva fecha.', 'error');
      return;
    }
    const generalDeadline = deadlines[extensionForm.period];
    if (generalDeadline && extensionForm.date <= generalDeadline) {
      onNotify(`La prórroga debe ser posterior a la fecha general (${generalDeadline}).`, 'error');
      return;
    }
    try {
      await onSetExtension(extensionForm.period, extensionForm.companyId, extensionForm.date);
      onNotify('Prórroga registrada para la empresa seleccionada.', 'success');
      setExtensionForm(prev => ({ ...prev, date: '' }));
    } catch (error) {
      onNotify(error.message || 'No se pudo registrar la prórroga.', 'error');
    }
  };

  const handleSendReminder = async () => {
    const company = companyOptions.find(item => item.id === extensionForm.companyId);
    if (!company || !extensionForm.period) {
      onNotify('Selecciona la empresa y el período del recordatorio.', 'error');
      return;
    }
    setIsSendingReminder(true);
    try {
      const result = await onSendReminder(company, extensionForm.period);
      onNotify(
        `Recordatorio aceptado por SendGrid para ${company.name}${result?.recipientEmail ? ` · ${result.recipientEmail}` : ''}.`,
        'success',
      );
    } catch (error) {
      onNotify(error.message || 'No se pudo enviar el recordatorio individual.', 'error');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleRemoveExtension = async (companyId) => {
    const company = companyOptions.find(item => item.id === companyId);
    if (!window.confirm(`¿Quitar la prórroga de ${company?.name || companyId} para ${extensionForm.period}?`)) return;
    try {
      await onRemoveExtension(extensionForm.period, companyId);
      onNotify('Prórroga retirada. La empresa volverá a usar la fecha general.', 'success');
    } catch (error) {
      onNotify(error.message || 'No se pudo retirar la prórroga.', 'error');
    }
  };

  const currentExtensions = extensions[extensionForm.period] || {};
  const companyOptions = users.filter(u => u.role === 'company');
  const upcomingDeadlines = Object.entries(deadlines || {})
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .slice(0, 4);

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">

        {/* Fecha límite */}
        <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm p-6 space-y-4">
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Plazos oficiales</p>
            <h2 className="text-lg font-semibold text-slate-900">Fecha límite · {currentMonth}</h2>
          </div>
          <input
            type="date"
            value={deadline}
            onChange={e => setDeadline(e.target.value)}
            className="block w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-medium text-slate-700 focus:outline-none focus:border-[#004AAD] focus:ring-2 focus:ring-[#004AAD]/10 transition-colors"
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSetDeadline}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#004AAD] hover:bg-[#003B8A] shadow-sm hover:shadow-md transition-all whitespace-nowrap"
            >
              Guardar fecha
            </button>
          </div>
          <p className="text-xs leading-relaxed text-slate-400">
            Esta fecha aplica a todas las empresas que no tengan una prórroga individual.
          </p>
        </div>

        {/* Prórrogas */}
        <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Flexibilizaciones</p>
              <h2 className="text-lg font-semibold text-slate-900">Prórrogas personalizadas</h2>
            </div>
            <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">
              {Object.keys(currentExtensions).length} activas
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-xs font-semibold text-slate-600">
              Empresa
              <select
                aria-label="Empresa para gestión individual"
                value={extensionForm.companyId}
                onChange={e => setExtensionForm(prev => ({ ...prev, companyId: e.target.value }))}
                className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-normal text-slate-700 focus:outline-none focus:border-[#004AAD] focus:ring-2 focus:ring-[#004AAD]/10 transition-colors"
              >
                <option value="">Selecciona empresa</option>
                {companyOptions.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}{company.active === false ? ' · desactivada' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Período
              <input
                aria-label="Período para gestión individual"
                type="month"
                value={extensionForm.period}
                onChange={e => setExtensionForm(prev => ({ ...prev, period: e.target.value }))}
                className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-normal text-slate-700 focus:outline-none focus:border-[#004AAD] focus:ring-2 focus:ring-[#004AAD]/10 transition-colors"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Nueva fecha límite
              <input
                aria-label="Nueva fecha límite individual"
                type="date"
                value={extensionForm.date}
                onChange={e => setExtensionForm(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-normal text-slate-700 focus:outline-none focus:border-[#004AAD] focus:ring-2 focus:ring-[#004AAD]/10 transition-colors"
              />
            </label>
            <div className="flex flex-col gap-2 pt-5 sm:flex-row">
              <button
                onClick={handleSetExtension}
                className="flex-1 rounded-xl bg-[#004AAD] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#003B8A] hover:shadow-md"
              >
                Guardar prórroga
              </button>
              <button
                onClick={handleSendReminder}
                disabled={isSendingReminder || !extensionForm.companyId}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSendingReminder ? 'Enviando...' : 'Recordar solo a esta empresa'}
              </button>
            </div>
          </div>
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-2">Prórrogas activas · {extensionForm.period}</p>
            {Object.keys(currentExtensions).length === 0 ? (
              <p className="text-sm text-slate-400">No hay prórrogas registradas.</p>
            ) : (
              <ul className="space-y-1.5">
                {Object.entries(currentExtensions).map(([companyId, date]) => {
                  const company = companyOptions.find(c => c.id === companyId);
                  return (
                    <li key={companyId} className="flex flex-col gap-2 rounded-lg border border-slate-100 bg-slate-50 px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="font-medium text-slate-800">{company?.name || companyId}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-xs font-medium text-slate-500">{date}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveExtension(companyId)}
                          className="text-xs font-semibold text-rose-600 hover:underline"
                        >
                          Quitar
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Calendario de hitos */}
      <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Calendario</p>
            <h2 className="text-lg font-semibold text-slate-900">Próximos hitos</h2>
          </div>
          <span className="text-xs font-medium text-slate-400">{upcomingDeadlines.length} meses programados</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {upcomingDeadlines.map(([month, date]) => {
            const isCurrent = month === currentMonth;
            const formattedDate = date
              ? new Date(date).toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })
              : 'Sin fecha';
            return (
              <div
                key={month}
                className={`rounded-xl border px-4 py-4 ${
                  isCurrent
                    ? 'border-[#004AAD]/25 bg-blue-50/60'
                    : 'border-slate-200/70 bg-slate-50/60'
                }`}
              >
                <p className={`text-[0.6rem] uppercase mb-1 ${isCurrent ? 'text-[#004AAD]/70' : 'text-slate-400'}`}>{month}</p>
                <p className={`text-2xl font-semibold ${isCurrent ? 'text-[#004AAD]' : 'text-slate-700'}`}>{formattedDate}</p>
                <p className={`text-xs mt-0.5 ${isCurrent ? 'text-[#004AAD]/60' : 'text-slate-400'}`}>{isCurrent ? 'Mes en curso' : 'Hito planificado'}</p>
              </div>
            );
          })}
          {!upcomingDeadlines.length && (
            <p className="text-sm text-slate-400 col-span-full">No hay fechas configuradas.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDeadlineSetter;
